import { Intent, MenuDivider, MenuItem } from '@blueprintjs/core';
import { Button, ButtonSave } from 'components/buttons';
import { CalendarCell } from 'components/calendar';
import { DateToLocal } from 'components/dates';
import { Dropdown } from 'components/dropdown';
import { FormCheckbox } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import moment from 'moment';
import { DetailedManualEntry } from 'pages/employee-settings/manual-entry-templates/shared/DetailedManualEntry';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ManualEntryService } from 'services';

const defaultArray = [];

export function MultiDayManualEntry({
    activities,
    onActivitiesUpdate,
    onValidChange,
    infringements = defaultArray,
    editMode
}) {

    const [actionDropdownItems, setActionDropdownItems] = useState([]);

    const daysOfWeek = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
    const [calendarDays, setCalendarDays] = useState(null);
    const [weeksToShow, setWeeksToShow] = useState(1);
    const daysInWeek = 7;
    const maxWeeksToDisplay = 8;
    const minutesInDay = 1440;
    const [applyingTemplate, setApplyingTemplate] = useState(false);

    //Hard coded monday to go from as dates do not matter
    const calendarStartDate = moment().startOf('isoWeek').format("YYYY-MM-DD");

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedTrace, setSelectedTrace] = useState(null);
    const [cellData, setCellData] = useState([]);
    const chargableColour = "#ff7454";
    const freeColour = "#C389DB";
    const [userTemplates, setUserTemplates] = useState([]);

    const [detailedEntryValid, setDetailedEntryValid] = useState(false);
    const [detailedManualEntry, setDetailedManualEntry] = useState([]);
    const [showDetailedEntryModal, setShowDetailedEntryModal] = useState(false);
    const [canDeleteData, setCanDeleteData] = useState(false);
    const [editTraceDetails, setEditTraceDetails] = useState(null);

    const [entriesDropdownItems, setEntriesDropdownItems] = useState([]);

    const [isValid, setIsValid] = useState(false);

    const activityTypes = [
        { id: "STATHOLIDAY", activityName: 'Statutory Holiday', name: 'Statutory Holiday', colour: chargableColour, startTime: "09:00", icon: "airplane" },
        { id: "EXTRAHOLIDAY", activityName: 'Extra holiday', name: 'Extra Holiday', colour: freeColour, startTime: "09:00", icon: "airplane" },
        { id: "REST", activityName: 'Rest', name: 'Rest Day', colour: freeColour, startTime: "09:00", icon: "chat" },
    ];

    const overnightActivityTypes = [
        { id: "STATHOLIDAY", activityName: 'Statutory Holiday', name: 'Overnight Statutory Holiday', colour: chargableColour, startTime: "20:00", icon: "moon" },
        { id: "REST", activityName: 'Rest', name: 'Overnight Rest Day', colour: freeColour, startTime: "20:00", icon: "moon" },
    ]

    const detailedEntry = { id: "DETAILED", name: 'Detailed Manual Entry', colour: chargableColour }

    useEffect(() => {
        const detaultPageSize = 25;
        ManualEntryService.getTemplateList(detaultPageSize, 1, "", "Name", "A", [{ name: "Template", value: "MANUALENTRYSINGLE" }, { name: "Visible", value: "True" }]).then((userTemplateResponse) => {
            setUserTemplates(userTemplateResponse.data);
        });
    }, []);

    function setTime(momentDate, dateString) {
        let timeParts = dateString.split(":");
        momentDate.set('hour', parseInt(timeParts[0], 10));
        momentDate.set('minute', parseInt(timeParts[1], 10));
        return momentDate;
    }

    useEffect(() => {

        function calculateCellTraceLenth(startDate, endDate) {
            const cellWidth = 174;
            return (moment.duration(endDate.diff(startDate)).asMinutes() / minutesInDay) * cellWidth;
        }

        let newCellData = [];

        activities.forEach(day => {

            let traces = [];
            let startDay = moment(calendarStartDate).add(day.dayNumber - 1, 'days').startOf('day');

            day.shifts.forEach(shift => {

                let shiftStartTime = setTime(moment(startDay), shift[0].time);
                let shiftDuration = shift.reduce((total, activity) => total + activity.duration, 0);
                let shiftEndTime = moment(shiftStartTime).add(shiftDuration, 'minutes');

                let endOvernight = shiftStartTime.format("YYYY-MM-DD") !== shiftEndTime.format("YYYY-MM-DD");
                let endTraceDayEndTime = endOvernight ? `${shiftEndTime.format("YYYY-MM-DD")} 00:00` : shiftEndTime.format("YYYY-MM-DD HH:mm");

                let traceActivities = shift.reduce((total, activity) => {
                    if(!total.some((t) => t === activity.type)) {
                        total.push(activity.type);
                    }

                    return total;
                }, []);

                let matchedActivity = traceActivities.length === 1 ? (activityTypes.find(x => x.id === traceActivities[0]) ?? detailedEntry) : detailedEntry;

                traces.push({
                    activityType: matchedActivity,
                    date: startDay.format("YYYY-MM-DD"),
                    shiftStartDate: shiftStartTime.format("YYYY-MM-DD HH:mm"),
                    shiftEndDate: shiftEndTime.format("YYYY-MM-DD HH:mm"),
                    dayStartTimeBst: shiftStartTime.format("YYYY-MM-DD HH:mm"),
                    dayEndTimeBst: endTraceDayEndTime,
                    overnight: endOvernight,
                    durationPercentage: calculateCellTraceLenth(shiftStartTime, moment(endTraceDayEndTime)),
                    leftPercent: calculateCellTraceLenth(startDay, shiftStartTime)
                });

            });

            newCellData.push({
                index: day.dayNumber,
                hasOvernight: traces.some(x => x.overnight),
                activityInfo: traces,
            })

        });

        //Map overnight data into the next cell
        //Take a clone of the array to iterate over, because we may be adding new days into the actual array

        const cellsToIterate = [...newCellData];

        cellsToIterate.forEach((day, dayIndex) => {
            if (day.hasOvernight) {
                const nextDay = day.index + 1;
                const nextDayData = newCellData.find(x => x.index === nextDay);

                const overnightData = day.activityInfo.find(x => x.overnight);

                let nextDayTrace = {
                    ...overnightData,
                    dayStartTimeBst: overnightData.dayEndTimeBst,
                    dayEndTimeBst: overnightData.shiftEndDate,
                    leftPercent: 0,
                    durationPercentage: calculateCellTraceLenth(moment(overnightData.shiftEndDate).startOf('day'), moment(overnightData.shiftEndDate)),
                    overnight: false
                }

                if (nextDayData == null) {
                    //Insert new day

                    let newDay = {
                        index: nextDay,
                        hasOvernight: false,
                        activityInfo: [nextDayTrace],
                    }

                    newCellData.splice(dayIndex + 1, 0, newDay);

                } else {
                    //Insert activity to the current day
                    nextDayData.activityInfo.unshift(nextDayTrace);
                }
            }
        });

        setCellData(newCellData);

    }, [activities]);

    useEffect(() => {

        let lastWeekBlank = false;

        if(cellData.length === 0) {
            setIsValid(false);
            return;
        }

        //Allow the template to be valid if there are blank weeks at the end of the template, but not in between filled weeks
        for (let i = 0; i < weeksToShow; i++) {
            const currentWeekHasData = cellData.some(x => x.index > (i * daysInWeek) && x.index < ((i + 1) * daysInWeek));
            
            if(currentWeekHasData && lastWeekBlank) {
                setIsValid(false);
                return;
            }

            lastWeekBlank = !currentWeekHasData;
        }

        setIsValid(true);

    }, [cellData, weeksToShow])

    useEffect(() => {

        setActionDropdownItems([
            <MenuItem key="action-insert-before" text="Insert row before" onClick={insertRowBefore} disabled={selectedRows.length !== 1 || weeksToShow >= maxWeeksToDisplay} />,
            <MenuItem key="action-insert-after" text="Insert row after" onClick={insertRowAfter} disabled={selectedRows.length !== 1 || weeksToShow >= maxWeeksToDisplay} />,
            <MenuItem key="action-delete-row" text="Delete row" onClick={deleteRows} disabled={selectedRows.length === 0} />,
        ]);

        mapAssetCalendar();

    }, [weeksToShow, selectedRows, selectedDays, cellData, selectedTrace, applyingTemplate, infringements]);

    useEffect(() => {
        setCanDeleteData(selectedTrace != null || (selectedDays.length > 0 && activities.some(activity => selectedDays.includes(activity.dayNumber))));
    }, [selectedDays, activities, selectedTrace]);

    useEffect(() => {
        //work out if any rows are set
        const rows = [];

        for (let i = 0; i < weeksToShow; i++) {

            let cells = Array.from({ length: daysInWeek }).fill(1);

            cells = cells.map((value, index) => {
                return (value + index) + (i * daysInWeek);
            });

            if (cells.every(c => selectedDays.includes(c))) {
                rows.push(i);
            }
        }

        setSelectedRows(rows);

    }, [selectedDays, weeksToShow]);

    useEffect(() => {

        const mappedStandardTypes = activityTypes.map((activity) => {
            return <MenuItem key={`add-${activity.name}`} text={activity.name} onClick={() => { applyStandardTemplate(activity) }} icon={activity.icon} />
        });

        const mappedOvernightTypes = overnightActivityTypes.map((activity) => {
            return <MenuItem key={`add-${activity.name}`} text={activity.name} onClick={() => { applyStandardTemplate(activity) }} icon={activity.icon} />
        });

        setEntriesDropdownItems([
            <MenuItem key="day-templates-option" text="Day Templates" icon="timeline-events" disabled={userTemplates.length === 0}>
                {userTemplates && userTemplates.map((userTemplate) => {
                    return <MenuItem key={userTemplate.id} text={userTemplate.name} onClick={() => { applyUserTemplate(userTemplate.id) }} />;
                })}
            </MenuItem>,
            ...mappedStandardTypes,
            <MenuDivider key="divider-1" />,
            ...mappedOvernightTypes
        ]);
    }, [userTemplates, selectedDays]);

    function applyStandardTemplate(activity) {
        if (selectedDays.length === 0) {
            return;
        }

        applyTraceData(selectedDays.map((dayIndex) => {
            return {
                dayNumber: dayIndex,
                activities: [{
                    type: activity.id,
                    time: activity.startTime,
                    duration: 480
                }]
            };
        }));

    }

    function applyTraceData(newActivities) {

        let successful = true;
        let clashCount = 0;
        let existingTraces = [];

        let startDay = moment(calendarStartDate).startOf('day');

        let cellDataList = cellData;

        if (editTraceDetails != null) {
            cellDataList = cellData.map((day) => {
                //Check the day and day after incase of overnight
                if (day.index === editTraceDetails.dayNumber || day.index === editTraceDetails.dayNumber + 1) {
                    const newDay = {
                        ...day,
                        activityInfo: [...day.activityInfo].filter(x => x.shiftStartDate !== editTraceDetails.shiftStartDate || x.shiftEndDate !== editTraceDetails.shiftEndDate)
                    }
                    return newDay.activityInfo.length > 0 ? newDay : null;
                }
                return day;
            }).filter(day => day != null);
        }

        //Create a global list of all current traces
        cellDataList.forEach(day => {
            existingTraces = [
                ...existingTraces,
                ...day.activityInfo.map((trace) => {
                    return {
                        start: moment.duration(moment(trace.shiftStartDate).diff(startDay)).asMinutes(),
                        end: moment.duration(moment(trace.shiftEndDate).diff(startDay)).asMinutes()
                    }
                })
            ]
        });

        //Check for clashing

        let clonedActivities = [
            ...activities,
        ];

        if (editTraceDetails != null) {
            clonedActivities = clonedActivities.map((day) => {
                if (day.dayNumber === editTraceDetails.dayNumber) {
                    let newDay = {
                        ...day,
                    };
                    newDay.shifts.splice(editTraceDetails.shiftIndex, 1)

                    return newDay.shifts.length > 0 ? newDay : null;
                }
                return day;
            }).filter(day => day != null);
        }

        newActivities.forEach(newActivity => {

            let newActivityStartDay = moment(calendarStartDate).add(newActivity.dayNumber - 1, 'days').startOf('day');
            let shiftStartDate = setTime(moment(newActivityStartDay), newActivity.activities[0].time);
            let shiftDuration = newActivity.activities.reduce((total, activity) => total + activity.duration, 0);
            let shiftOffset = moment.duration(moment(shiftStartDate).diff(startDay)).asMinutes();

            let shift = {
                start: shiftOffset,
                end: shiftOffset + shiftDuration
            };

            const overlapping = existingTraces.some((trace) => {
                return (shift.start > trace.start && shift.start < trace.end) || (shift.end > trace.start && shift.end < trace.end) || (trace.start <= shift.start && trace.end >= shift.end) || (shift.start <= trace.start && shift.end >= trace.end);
            });

            if (overlapping) {
                clashCount++;
            } else {

                const existingDay = clonedActivities.find(x => x.dayNumber === newActivity.dayNumber);

                if (existingDay == null) {
                    clonedActivities.push({
                        dayNumber: newActivity.dayNumber,
                        shifts: [
                            [...newActivity.activities]
                        ]
                    });
                } else {

                    const newTimeParts = newActivity.activities[0].time.split(":");
                    const newStartHour = parseInt(newTimeParts[0], 10);
                    const newStartMinute = parseInt(newTimeParts[1], 10);

                    //Find where to insert the new activity
                    const insertIndex = existingDay.shifts.findIndex((x) => {
                        const currentTimeParts = x[0].time.split(":");
                        const currentStartHour = parseInt(currentTimeParts[0], 10);
                        const currentStartMinute = parseInt(currentTimeParts[1], 10);

                        return (currentStartHour > newStartHour) || (currentStartHour === newStartHour && currentStartMinute > newStartMinute);
                    });

                    existingDay.shifts.splice(insertIndex === -1 ? existingDay.shifts.length : insertIndex, 0, newActivity.activities);
                };
            }

        });

        if (clashCount === newActivities.length) {
            NotificationToaster.show(Intent.WARNING, "New acitivities could not be applied because of clashing data");
            successful = false;
        } else if (clashCount > 0) {
            NotificationToaster.show(Intent.WARNING, "Only some acitivities have been applied because of clashing data");
        }

        if (successful) {
            onActivitiesUpdate(clonedActivities);
            
            clearSelection();
            setEditTraceDetails(null);
        }
        return successful;
    }

    function mapAssetCalendar() {

        let calendarMonth = [];
        let weeksToRender = weeksToShow;


        let maxWeek = Math.ceil(cellData.reduce((currentMax, activity) => Math.max(currentMax, activity.index), 0) / daysInWeek);

        if (maxWeek === 0 && weeksToRender === 0) {
            weeksToRender = 1;
        }

        if (maxWeek > weeksToRender) {
            weeksToRender = maxWeek;
            setWeeksToShow(maxWeek);
        }

        if (maxWeek <= maxWeeksToDisplay && weeksToRender > maxWeeksToDisplay) {
            weeksToRender = maxWeeksToDisplay;
            setWeeksToShow(maxWeeksToDisplay);
        }

        for (let i = 0; i < weeksToRender; i++) {
            calendarMonth.push(mapAssetCalendarWeekJsx(i));
        }

        setCalendarDays(<div className="calendar-month">
            {calendarMonth.map(input => { return input })}
        </div>);
    }

    function mapAssetCalendarWeekJsx(weekNumber) {
        let calendarWeek = [];
        for (let i = 0; i < daysInWeek; i++) {

            const cellIndex = (weekNumber * daysInWeek) + (i + 1);

            const matchedActivity = cellData.find(x => x.index === cellIndex)?.activityInfo ?? [];

            let day = {
                index: cellIndex,
                date: moment(calendarStartDate).add(cellIndex - 1, 'days').format("YYYY-MM-DD"),
                activityInfo: matchedActivity,
                selected: selectedDays.some(x => x === cellIndex)
            };

            const rulesEngineStartDate = -8;
            const rulesEngineDate = moment(calendarStartDate).add(cellIndex - 1, 'days').add(rulesEngineStartDate, 'weeks').format("YYYY-MM-DD")

            //occurredAt is a UTC instant, so it has to be converted before matching the calendar date.
            //An infringement just after local midnight belongs to the later day, not the UTC one.
            const dayInfringements = infringements.filter(x => moment.utc(x.occurredAt).local().format("YYYY-MM-DD") === rulesEngineDate);

            let cellIcons = [];

            if (dayInfringements.length > 0) {
                cellIcons.push({
                    icon: "warning-sign",
                    clickEvent: () => { },
                    tooltipContent: <><p>The following infrigements occured: </p><ul>{dayInfringements.map((infr) => <li key={infr.code}>{infr.code} occurs at <DateToLocal format="HH:mm">{infr.occurredAt}</DateToLocal></li>)}</ul></>,
                    colour: "#ec2d6d",
                    evaluator: true
                });
            }

            calendarWeek.push(mapAssetCalendarDayJsx(day, weekNumber % 2 === 0, cellIcons));
        }
        return (<div className="calendar-week" key={`calendar-week-${weekNumber}`} >
            {calendarWeek.map(input => { return input })}
            <div className="calendar-cell-select-week">
                <ShowHide
                    evaluator={weekNumber < maxWeeksToDisplay}
                    show={
                        <FormCheckbox
                            checked={selectedRows.some(x => x === weekNumber)}
                            label=''
                            id={`week-checkbox-${weekNumber}`}
                            key={`week-checkbox-${weekNumber}`}
                            onChange={() => toggleWeekSelect(weekNumber)}
                            disabled={applyingTemplate}
                            name={`week-checkbox-${weekNumber}`}
                        />
                    }
                />
            </div>
        </div>);
    }

    function mapAssetCalendarDayJsx(dayData, weekOdd, cellIcons) {
        return (
            <CalendarCell
                dayIndex={dayData.index}
                data={dayData}
                weekOdd={weekOdd}
                handleDaySelectedChange={toggleDaySelect}
                handleActivitySelectedChange={toggleActivitySelect}
                isSelected={dayData.selected}
                selectedActivity={selectedTrace}
                showTimes
                key={`calendar-cell-${dayData.index}`}
                cellIcons={cellIcons}
                displayDateFormat="HH:mm"
                displayDate={false}
                datesAreUtc={false}
            />
        );
    }

    function addNewWeek() {
        setWeeksToShow((prev) => prev + 1);
    }

    function toggleWeekSelect(weekNumber) {

        let cells = Array.from({ length: daysInWeek }).fill(1);

        cells = cells.map((value, index) => {
            return (value + index) + (weekNumber * daysInWeek);
        });

        if (selectedRows.some(x => x === weekNumber)) {
            setSelectedDays((prev) => {
                return prev.filter(x => !cells.some(c => c === x));
            });
        } else {
            setSelectedDays((prev) => {
                return [
                    ...prev,
                    ...cells.filter(c => !prev.some(p => p === c))
                ];
            });
        }

        setSelectedTrace(null);
    }

    function toggleDaySelect(e, cellNumber) {

        if (cellNumber > (maxWeeksToDisplay * daysInWeek) || applyingTemplate) {
            return;
        }

        setSelectedDays((prev) => {

            let clonedDays = [...prev];

            if (clonedDays.some(x => x === cellNumber)) {
                clonedDays = clonedDays.filter(x => x !== cellNumber);
            } else {
                clonedDays.push(cellNumber);
            }
            return clonedDays;
        });
        setSelectedTrace(null);
    }

    function toggleActivitySelect(e, activity) {
        e.stopPropagation();

        if (applyingTemplate) {
            return;
        }

        setSelectedTrace((prev) => {
            if (prev == null || (prev.shiftStartDate !== activity.shiftStartDate || prev.shiftEndDate !== activity.shiftEndDate)) {
                return activity;
            }

            return null;
        });
        setSelectedDays([]);
    }

    function deleteRows() {

        if (selectedRows.length === 0) {
            return;
        }

        const selectedRowCount = selectedRows.length;
        deleteAllActivities(true);

        //Force into next render cycle to allow activities to update first
        setTimeout(function () {
            setWeeksToShow((prev) => {
                return prev - selectedRowCount;
            });
            clearSelection();
        });

    }

    function insertRowBefore() {
        if (selectedRows.length !== 1) {
            return;
        }

        shiftActivitiesForNewRow((selectedRows[0] * daysInWeek))
        setWeeksToShow((prev) => prev + 1);
        clearSelection();
    }

    function insertRowAfter() {
        if (selectedRows.length !== 1) {
            return;
        }

        shiftActivitiesForNewRow((selectedRows[0] + 1) * daysInWeek)
        setWeeksToShow((prev) => prev + 1);
        clearSelection();

    }

    function shiftActivitiesForNewRow(endOfWeek) {

        let clonedActivities = [...activities];

        clonedActivities.forEach(activity => {
            if (activity.dayNumber > endOfWeek) {
                activity.dayNumber += daysInWeek;
            }
        });

        onActivitiesUpdate(clonedActivities);
    }

    function deleteSelectedActivities() {

        let clonedActivities = [...activities];

        if (selectedTrace != null) {

            //work out the day number we are on
            const traceDay = 1 + moment(selectedTrace.date).diff(moment(calendarStartDate), 'days');
            const foundDay = clonedActivities.find(x => x.dayNumber === traceDay);

            if (foundDay) {

                let clonedDayTraces = [...foundDay.shifts];
                let shiftIndex = clonedDayTraces.findIndex((trace) => trace[0].time === moment(selectedTrace.shiftStartDate).format("HH:mm"));

                if(shiftIndex > -1) {
                    clonedDayTraces.splice(shiftIndex, 1);
                }


                if (clonedDayTraces.length === 0) {
                    //We can now remove the day
                    clonedActivities = clonedActivities.filter(x => x.dayNumber !== traceDay);
                } else {
                    foundDay.shifts = clonedDayTraces;
                }
            }


        } else {
            clonedActivities = clonedActivities.filter(x => !selectedDays.some(day => day === x.dayNumber));
        }

        onActivitiesUpdate(clonedActivities);
        clearSelection();
    }

    function deleteAllActivities(shiftActivities = false) {

        let clonedActivities = [...activities];

        selectedRows.forEach(row => {
            const startDay = (row * daysInWeek) + 1;
            const endDay = (row + 1) * daysInWeek;

            clonedActivities = clonedActivities.filter(x => x.dayNumber < startDay || x.dayNumber > endDay);
        });

        if (shiftActivities) {

            selectedRows.forEach(row => {
                const startDay = (row * daysInWeek) + 1;
                clonedActivities.forEach(activity => {
                    if (activity.dayNumber > startDay) {
                        activity.dayNumber -= daysInWeek;
                    }
                });

            });

        }

        onActivitiesUpdate(clonedActivities);
    }

    function onDetailedManualEntryChange(detailedActivities) {
        setDetailedManualEntry(detailedActivities);
    }

    function onDetailedModalClose() {
        setShowDetailedEntryModal(false);
        setDetailedManualEntry([]);
        setEditTraceDetails(null);
    }

    function onDetailedModalOpen() {
        setShowDetailedEntryModal(true);
    }

    function onDetailedEntrySave() {

        let successful = true;

        if (editTraceDetails != null) {
            //We have been editing a trace
            successful = applyTraceData([{
                dayNumber: editTraceDetails.dayNumber,
                activities: detailedManualEntry
            }]);

        } else {
            //We are adding a new detailed trace
            successful = applyTraceData(selectedDays.map((dayIndex) => {
                return {
                    dayNumber: dayIndex,
                    activities: detailedManualEntry
                };
            }));
        }

        if (successful) {
            onDetailedModalClose();
            clearSelection();
        }

    }

    function clearSelection() {
        setSelectedDays([]);
        setSelectedTrace(null);
    }

    function applyUserTemplate(templateId) {
        setApplyingTemplate(true);
        ManualEntryService.getTemplate("MANUALENTRYSINGLE", templateId).then((templateData) => {
            applyTraceData(selectedDays.map((dayIndex) => {
                return {
                    dayNumber: dayIndex,
                    activities: templateData.activities
                };
            }));
        }).finally(() => {
            setApplyingTemplate(false);
        })
    }

    function onDetailedEntryEdit() {
        if (selectedTrace != null) {

            let clonedActivities = [...activities];
            //work out the day number we are on
            const traceDay = 1 + moment(selectedTrace.date).diff(moment(calendarStartDate), 'days');
            const foundDay = clonedActivities.find(x => x.dayNumber === traceDay);

            if (foundDay) {

                let clonedDayTraces = [...foundDay.shifts];
                let shiftIndex = clonedDayTraces.findIndex((trace) => trace[0].time === moment(selectedTrace.shiftStartDate).format("HH:mm"));

                const detailedActivities = clonedDayTraces[shiftIndex];
                setDetailedManualEntry(detailedActivities);
                setShowDetailedEntryModal(true);
                setEditTraceDetails({
                    dayNumber: traceDay,
                    shiftStartDate: selectedTrace.shiftStartDate,
                    shiftEndDate: selectedTrace.shiftEndDate,
                    shiftIndex: shiftIndex,
                });
            }
        }
    }

    useEffect(function () {
        onValidChange(isValid && !applyingTemplate);
    }, [isValid, applyingTemplate]);

    return (
        <>
            <h2>{editMode ? "Edit Template" : "Build Template"}</h2>
            <div className="button-row button-row-stacked-mobile spacer-bottom">
                <Dropdown
                    items={entriesDropdownItems}
                    intent="Primary"
                    text="Standard Entries"
                    disabled={selectedDays.length === 0 || applyingTemplate}
                    popoverUsePortal={false} />
                <Button intent="primary" text="Detailed Entry" onClick={onDetailedModalOpen} disabled={selectedDays.length === 0 || applyingTemplate} />
                <Button intent="primary" text="Edit" onClick={onDetailedEntryEdit} disabled={selectedTrace == null || applyingTemplate} />
                <Button intent="danger" text="Delete" onClick={deleteSelectedActivities} icon="trash" disabled={!canDeleteData || applyingTemplate} />
                <Dropdown
                    disabled={selectedRows.length === 0 || applyingTemplate}
                    items={actionDropdownItems}
                    intent="Primary"
                    text="Actions"
                    popoverUsePortal={false} />
            </div>
            <div className="calendar-week spacer-bottom calendar-compact">
                {daysOfWeek.map(function (dayName) {
                    return (
                        <div className="day-of-week-header" key={dayName}>{dayName}</div>
                    );
                })}

                {calendarDays}
            </div>
            <div className='hierarchy-add-child-container spacer-bottom'>
                <Tooltip content={"The maximum number of allowed weeks has been reached"} usePortal disabled={weeksToShow < maxWeeksToDisplay}>
                    <Button text="ADD WEEK" icon='plus' className='hierarchy-drop  hierarchy-add-child' onClick={addNewWeek} disabled={(weeksToShow >= maxWeeksToDisplay) || applyingTemplate} />
                </Tooltip>
            </div>
            <Modal
                isOpen={showDetailedEntryModal}
                size={ModalSize.LARGE}
                showCloseButton={false}
                clickOutsideClose={false}
                overflowAuto
                title="Detailed Manual Entry"
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <ButtonSave icon={null} text="Save and close" intent="primary" onClick={onDetailedEntrySave} simpleDisabled={!detailedEntryValid} id="detailed-confirm-button" />
                        <Button text="Cancel" intent="secondary" onClick={onDetailedModalClose} id="detailed-cancel-button"></Button>
                    </div>
                )}
            >
                <DetailedManualEntry originalActivities={detailedManualEntry} onValidChange={setDetailedEntryValid} onActivitiesChange={onDetailedManualEntryChange} />
            </Modal>
        </>
    );
}

MultiDayManualEntry.propTypes = {
    activities: PropTypes.array.isRequired,
    onValidChange: PropTypes.func.isRequired,
    onActivitiesUpdate: PropTypes.func,
    infringements: PropTypes.array,
    editMode: PropTypes.bool.isRequired
};